import React, { useState, useEffect } from 'react';
import { ref, onValue } from 'firebase/database';
import { database } from '../../firebase/config';
import { FaUser, FaCalendarAlt, FaStar, FaSearch, FaEnvelope, FaPhone } from 'react-icons/fa';
import './Admin.css';

const GestionClients = () => {
  const [clients, setClients] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const rendezvousRef = ref(database, 'rendezvous');
    const unsubscribe = onValue(rendezvousRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const clientsMap = new Map();
        
        Object.entries(data).forEach(([userId, userRdv]) => {
          const rdvs = Object.values(userRdv);
          if (rdvs.length > 0) {
            const client = rdvs[0];
            clientsMap.set(userId, {
              id: userId,
              nom: client.nom || 'Client',
              email: client.email || 'Non renseigné',
              telephone: client.telephone || 'Non renseigné',
              totalRdvs: rdvs.length,
              dernierRdv: new Date(Math.max(...rdvs.map(r => new Date(r.date)))),
              statuts: rdvs.reduce((acc, r) => {
                acc[r.statut] = (acc[r.statut] || 0) + 1;
                return acc;
              }, {})
            });
          }
        });
        
        setClients(Array.from(clientsMap.values()));
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const filteredClients = clients.filter(client =>
    client.nom?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    client.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Chargement des clients...</p>
      </div>
    );
  }

  return (
    <div className="gestion-container">
      <div className="page-header">
        <h2>Gestion des clients</h2>
        <p className="page-subtitle">Consultez tous vos clients</p>
      </div>
      
      <div className="search-bar">
        <FaSearch className="search-icon" />
        <input
          type="text"
          placeholder="Rechercher par nom, email ou téléphone..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        {searchTerm && (
          <span className="search-count">{filteredClients.length} client(s) trouvé(s)</span>
        )}
      </div>

      <div className="clients-stats">
        <div className="stat-card-mini">
          <span className="stat-value">{clients.length}</span>
          <span className="stat-label">Clients total</span>
        </div>
        <div className="stat-card-mini">
          <span className="stat-value">
            {clients.reduce((acc, c) => acc + c.totalRdvs, 0)}
          </span>
          <span className="stat-label">Rendez-vous total</span>
        </div>
      </div>

      {filteredClients.length === 0 ? (
        <div className="no-data">
          <FaUser className="no-data-icon" />
          <p>Aucun client trouvé</p>
          <span>Essayez une autre recherche</span>
        </div>
      ) : (
        <div className="clients-grid">
          {filteredClients.map(client => (
            <div key={client.id} className="client-card">
              {/* En-tête avec nom et avatar */}
              <div className="client-header">
                <div className="client-avatar">
                  {client.nom?.charAt(0).toUpperCase()}
                </div>
                <div className="client-title">
                  <h3>{client.nom}</h3>
                  <span className="client-id">ID: {client.id.substring(0, 8)}...</span>
                </div>
              </div>
              
              {/* Informations ligne par ligne */}
              <div className="client-info-vertical">
                <div className="info-line">
                  <div className="info-line-icon">
                    <FaEnvelope />
                  </div>
                  <div className="info-line-content">
                    <span className="info-line-label">Email</span>
                    <span className="info-line-value">{client.email}</span>
                  </div>
                </div>

                <div className="info-line">
                  <div className="info-line-icon">
                    <FaPhone />
                  </div>
                  <div className="info-line-content">
                    <span className="info-line-label">Téléphone</span>
                    <span className="info-line-value">{client.telephone}</span>
                  </div>
                </div>

                <div className="info-line">
                  <div className="info-line-icon">
                    <FaCalendarAlt />
                  </div>
                  <div className="info-line-content">
                    <span className="info-line-label">Dernier RDV</span>
                    <span className="info-line-value">{client.dernierRdv.toLocaleDateString('fr-FR')}</span>
                  </div>
                </div>

                <div className="info-line">
                  <div className="info-line-icon">
                    <FaStar />
                  </div>
                  <div className="info-line-content">
                    <span className="info-line-label">Rendez-vous total</span>
                    <span className="info-line-value">{client.totalRdvs} rendez-vous</span>
                  </div>
                </div>
              </div>
              
              {/* Badges de statut */}
              <div className="client-stats">
                {Object.entries(client.statuts).map(([statut, count]) => (
                  <span key={statut} className={`statut-badge ${statut}`}>
                    {statut === 'en attente' && ''}
                    {statut === 'confirmé' && ''}
                    {statut === 'terminé' && ''}
                    {statut}: {count}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default GestionClients;